import moment from "moment";
import { supabase } from "../utils/supabaseClient";
import { shouldShowOnCorporateEnrollForm } from "./corporateEnrollHelpers";
import { fetchTemplateFieldMeta } from "./fetchTemplateFieldMeta";

/**
 * Resolve session venue_id / room_id (stored as codes) to display names via venues / rooms tables.
 * Same pattern as EachCourse.js / EnrolledCourses.js.
 */
async function buildSessionDisplayList(rawRows) {
    if (!rawRows?.length) return [];

    const roomCodes = [
        ...new Set(rawRows.filter((s) => s.room_id && !s.custom_room).map((s) => s.room_id)),
    ];
    const roomMap = {};
    if (roomCodes.length > 0) {
        const { data: rooms } = await supabase.from("rooms").select("code, name").in("code", roomCodes);
        rooms?.forEach((r) => {
            roomMap[r.code] = r;
        });
    }

    const venueCodes = [
        ...new Set(rawRows.filter((s) => s.venue_id && !s.custom_venue).map((s) => s.venue_id)),
    ];
    const venueMap = {};
    if (venueCodes.length > 0) {
        const { data: venues } = await supabase.from("venues").select("code, name").in("code", venueCodes);
        venues?.forEach((v) => {
            venueMap[v.code] = v;
        });
    }

    return rawRows.map((row) => ({
        date: row.session_date,
        start_time: row.start_time ? moment(row.start_time, "HH:mm:ss").format("HHmm") : null,
        end_time: row.end_time ? moment(row.end_time, "HH:mm:ss").format("HHmm") : null,
        venue:
            row.custom_venue ||
            (row.venue_id && venueMap[row.venue_id]?.name) ||
            row.venue_id ||
            null,
        room:
            row.custom_room ||
            (row.room_id && roomMap[row.room_id]?.name) ||
            row.room_id ||
            null,
    }));
}

/**
 * Load course/cert UI context + template field lists for a corporate enrol run.
 * @returns {Promise<{ missing: true } | { missing: false, runLabel: string, templateId: string|null, courseSummary: object, sessionsFlat: array, moduleSessions: array }>}
 */
export async function loadCorporateEnrollRunCourseContext(runKind, runCode, runLabelHint) {
    let templateId = null;
    let courseSummary = null;
    let sessionsFlat = [];
    let moduleSessions = [];
    let runLabel = runLabelHint || runCode;

    if (runKind === "course_run") {
        const { data: cr, error: crErr } = await supabase
            .from("course_run")
            .select(
                `
          *,
          course_type ( code, name, short_desc, registration_template_id, ssg_course, certification, school )
        `,
            )
            .eq("id", runCode)
            .single();
        if (crErr || !cr) {
            return { missing: true };
        }
        templateId = cr.registration_template_id || cr.course_type?.registration_template_id || null;
        runLabel = cr.course_type?.name || runLabel;
        courseSummary = {
            kind: "course_run",
            title: cr.course_type?.name || runLabel,
            subtitle: cr.id,
            description: cr.course_type?.short_desc || "",
            start_date: cr.start_date,
            end_date: cr.end_date,
            venue: cr.venue,
            ssg_course: cr.course_type?.ssg_course,
        };

        const { data: sess } = await supabase
            .from("session")
            .select("*")
            .eq("course_run_code", runCode)
            .order("session_date", { ascending: true });
        sessionsFlat = await buildSessionDisplayList(sess || []);
    } else if (runKind === "full_cert_run") {
        const { data: fcr, error: fcrErr } = await supabase
            .from("full_certification_runs")
            .select(
                `
          *,
          full_certification ( code, name, description, registration_template_id )
        `,
            )
            .eq("id", runCode)
            .single();
        if (fcrErr || !fcr) {
            return { missing: true };
        }
        templateId =
            fcr.registration_template_id || fcr.full_certification?.registration_template_id || null;
        runLabel = fcr.run_name || fcr.full_certification?.name || runLabel;
        courseSummary = {
            kind: "full_cert_run",
            title: fcr.run_name || fcr.full_certification?.name || runLabel,
            subtitle: fcr.id,
            description: fcr.full_certification?.description || "",
            start_date: fcr.start_date,
            end_date: fcr.end_date,
            venue: null,
            ssg_course: false,
        };

        const { data: links } = await supabase
            .from("full_cert_many_course_run")
            .select("course_run_code, course_run ( id, course_type ( name ) )")
            .eq("cert_run_code", runCode);

        const codes = (links || []).map((l) => l.course_run_code).filter(Boolean);
        if (codes.length === 0) {
            sessionsFlat = [];
            moduleSessions = [];
        } else {
            const { data: allSess } = await supabase
                .from("session")
                .select("*")
                .in("course_run_code", codes)
                .order("session_date", { ascending: true });

            const displaySessions = await buildSessionDisplayList(allSess || []);
            const byModule = {};
            (allSess || []).forEach((row, idx) => {
                const code = row.course_run_code;
                if (!byModule[code]) byModule[code] = [];
                byModule[code].push(displaySessions[idx]);
            });

            moduleSessions = (links || []).map((l) => {
                const cr = l.course_run;
                const label = cr?.course_type?.name
                    ? `${cr.course_type.name} (${l.course_run_code})`
                    : l.course_run_code;
                return {
                    label,
                    sessions: byModule[l.course_run_code] || [],
                };
            });
        }
    } else {
        return { missing: true };
    }

    let templateFieldIds;
    let templateFieldRecords;
    if (!templateId) {
        const { data: af } = await supabase
            .from("additional_registration_fields")
            .select("*")
            .or("field_status.is.null,field_status.eq.active")
            .order("section_order", { ascending: true })
            .order("display_order", { ascending: true });
        const allowed = (af || []).filter(shouldShowOnCorporateEnrollForm);
        templateFieldIds = null;
        templateFieldRecords = allowed;
    } else {
        const { ids, records } = await fetchTemplateFieldMeta(templateId);
        templateFieldIds = ids.length > 0 ? ids : [];
        templateFieldRecords = records;
    }

    return {
        missing: false,
        runLabel,
        templateFieldIds,
        templateFieldRecords,
        courseSummary,
        sessionsFlat,
        moduleSessions,
    };
}
